/**
 * ContentCalendarToolbar — mode-aware controls above the grid.
 *
 * Shared controls: mode toggle, "Today" jump, platform filter, Add
 * Publication button. Mode-B-only: prev/next window + window size
 * selector (1/2/4/8 weeks).
 */

import React from 'react';
import {
  Box, Button, IconButton, MenuItem, Select, Stack, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import AddIcon from '@mui/icons-material/Add';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import type { PlatformId } from '../../types';

export type CalendarToolbarMode = 'strategy' | 'cross-plan';

export const WINDOW_SIZE_OPTIONS: number[] = [1, 2, 4, 8];

export interface ContentCalendarToolbarProps {
  mode: CalendarToolbarMode;
  onModeChange: (mode: CalendarToolbarMode) => void;
  windowLabel: string;
  weekCount: number;
  onWeekCountChange?: (n: number) => void;
  onPrevWindow?: () => void;
  onNextWindow?: () => void;
  onJumpToday?: () => void;
  platformFilter: Set<PlatformId>;
  platformOptions: PlatformId[];
  onPlatformFilterChange: (next: Set<PlatformId>) => void;
  onAddPublication?: () => void;
  readOnly?: boolean;
  canSwitchToCrossPlan: boolean;
}

export const ContentCalendarToolbar: React.FC<ContentCalendarToolbarProps> = ({
  mode, onModeChange, windowLabel, weekCount, onWeekCountChange,
  onPrevWindow, onNextWindow, onJumpToday, platformFilter, platformOptions, onPlatformFilterChange,
  onAddPublication, readOnly = false, canSwitchToCrossPlan,
}) => {
  return (
    <Stack
      data-testid={CONTENT_PLAN.CALENDAR_TOOLBAR}
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: 1.5, flexWrap: 'wrap' }}
    >
      <ToggleButtonGroup
        data-testid={CONTENT_PLAN.CALENDAR_MODE_TOGGLE}
        value={mode}
        exclusive
        size="small"
        onChange={(_, v) => v && onModeChange(v as CalendarToolbarMode)}
      >
        <ToggleButton value="strategy" data-testid={CONTENT_PLAN.CALENDAR_MODE_STRATEGY}>
          Strategy plan
        </ToggleButton>
        <ToggleButton
          value="cross-plan"
          data-testid={CONTENT_PLAN.CALENDAR_MODE_CROSS}
          disabled={!canSwitchToCrossPlan}
        >
          Cross-plan
        </ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{windowLabel}</Typography>
      </Box>

      {mode === 'cross-plan' && (
        <>
          <IconButton
            size="small"
            data-testid={CONTENT_PLAN.CALENDAR_PREV_WINDOW}
            onClick={onPrevWindow}
            aria-label="Previous window"
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            data-testid={CONTENT_PLAN.CALENDAR_NEXT_WINDOW}
            onClick={onNextWindow}
            aria-label="Next window"
          >
            <ChevronRightIcon />
          </IconButton>
          <Select
            data-testid={CONTENT_PLAN.CALENDAR_WINDOW_SIZE}
            size="small"
            value={weekCount}
            onChange={(e) => onWeekCountChange?.(Number(e.target.value))}
            sx={{ minWidth: 100 }}
          >
            {WINDOW_SIZE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>{n} week{n > 1 ? 's' : ''}</MenuItem>
            ))}
          </Select>
        </>
      )}

      <Button
        size="small"
        variant="text"
        startIcon={<TodayIcon />}
        data-testid={CONTENT_PLAN.CALENDAR_JUMP_TODAY}
        onClick={onJumpToday}
      >
        Today
      </Button>

      <ToggleButtonGroup
        data-testid={CONTENT_PLAN.CALENDAR_PLATFORM_FILTER}
        value={Array.from(platformFilter)}
        size="small"
        onChange={(_, v) => onPlatformFilterChange(new Set(v as PlatformId[]))}
      >
        {platformOptions.map((p) => (
          <ToggleButton key={p} value={p} selected={platformFilter.has(p)}>
            {p[0].toUpperCase()}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Button
        size="small"
        variant="contained"
        startIcon={<AddIcon />}
        data-testid={CONTENT_PLAN.CALENDAR_ADD_PUBLICATION}
        onClick={onAddPublication}
        disabled={readOnly}
      >
        Publication
      </Button>
    </Stack>
  );
};
